import GoogleAd1 from "@/components/common/GoogleAd1";
import {
  MqsInterstitialStatus,
  resetMqsInterstitialGate,
  updateMqsInterstitialGate,
} from "@/components/common/GoogleAd1/mqsInterstitialGate";
import { getSearchParam } from "@/utils";

export const MQS_INTERSTITIAL_COOLDOWN_MS = 1_800_000;
export const MQS_INTERSTITIAL_DEBUG_QUERY = "mqs_debug";

interface MqsInterstitialControllerProps {
  config: any;
  enabled: boolean;
  requestKey: number;
}

const isLocalDebugHost = (hostName: string) => hostName === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostName);
const MQS_DEBUG_HOSTS = ["test.comfortweather.com", "test1.comfortweather.com"];

const formatTimestamp = (timestamp: number | null) => {
  if (!timestamp) {
    return "--";
  }
  return new Date(timestamp).toLocaleTimeString();
};

function MqsInterstitialDebugOverlay(props: {
  status: MqsInterstitialStatus;
  lastRequestAt: number | null;
  nextRequestAt: number | null;
  now: number;
  canRequestOnNavigation: boolean;
}) {
  const { status, lastRequestAt, nextRequestAt, now, canRequestOnNavigation } = props;
  const remainingMs = nextRequestAt ? Math.max(0, nextRequestAt - now) : 0;
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const navigationGate = nextRequestAt ? "cooldown" : canRequestOnNavigation ? "ready" : "waiting-fill";

  return (
    <div
      style={{
        position: "fixed",
        top: "12px",
        right: "12px",
        zIndex: 100000,
        minWidth: "180px",
        padding: "10px 12px",
        borderRadius: "12px",
        background: "rgba(0, 0, 0, 0.78)",
        color: "#fff",
        fontSize: "12px",
        lineHeight: 1.5,
        fontFamily: "monospace",
        pointerEvents: "none",
      }}
    >
      <div>m-qs interstitial</div>
      <div>status: {status}</div>
      <div>cooldown: {MQS_INTERSTITIAL_COOLDOWN_MS}ms</div>
      <div>remaining: {remainingMs > 0 ? `${remainingSeconds}s` : "0s"}</div>
      <div>next request gate: {navigationGate}</div>
      <div>last request: {formatTimestamp(lastRequestAt)}</div>
      <div>next request: {formatTimestamp(nextRequestAt)}</div>
    </div>
  );
}

/**
 * m-qs 全屏广告控制器：
 * 1. 只在该域名下维护单独的 GPT interstitial 状态机。
 * 2. 首次进入预加载一次；填充成功后进入冷却，冷却结束等待下一次切页再请求。
 */
export default function MqsInterstitialController(props: MqsInterstitialControllerProps) {
  const { config, enabled, requestKey } = props;
  const [status, setStatus] = useState<MqsInterstitialStatus>("idle");
  const [mountedRequestKey, setMountedRequestKey] = useState(0);
  const [lastRequestAt, setLastRequestAt] = useState<number | null>(null);
  const [nextRequestAt, setNextRequestAt] = useState<number | null>(null);
  const [canRequestOnNavigation, setCanRequestOnNavigation] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const requestSeqRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const lastHandledNavigationRequestKeyRef = useRef(0);
  const cooldownStartedRequestKeyRef = useRef(0);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const stopRequesting = useCallback((nextStatus: MqsInterstitialStatus) => {
    clearRetryTimer();
    cooldownStartedRequestKeyRef.current = 0;
    setMountedRequestKey(0);
    setNextRequestAt(null);
    setCanRequestOnNavigation(false);
    setStatus(nextStatus);
  }, [clearRetryTimer]);

  const releaseCurrentInterstitial = useCallback((nextStatus: MqsInterstitialStatus) => {
    setMountedRequestKey(0);
    setStatus(nextStatus);
  }, []);

  const beginRequest = useCallback(() => {
    if (!enabled || !config) {
      return;
    }

    clearRetryTimer();
    requestSeqRef.current += 1;
    cooldownStartedRequestKeyRef.current = 0;
    const requestAt = Date.now();
    setLastRequestAt(requestAt);
    setNextRequestAt(null);
    setCanRequestOnNavigation(false);
    setStatus("loading");
    setMountedRequestKey(requestSeqRef.current);
  }, [clearRetryTimer, config, enabled]);

  const scheduleNextNavigationRequest = useCallback(
    (delayMs: number, requestSeq: number) => {
      if (cooldownStartedRequestKeyRef.current === requestSeq) {
        return;
      }

      cooldownStartedRequestKeyRef.current = requestSeq;
      clearRetryTimer();
      const nextAt = Date.now() + delayMs;
      setNextRequestAt(nextAt);
      setCanRequestOnNavigation(false);
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        setNextRequestAt(null);
        setCanRequestOnNavigation(true);
      }, delayMs);
    },
    [clearRetryTimer],
  );

  useEffect(() => {
    if (!enabled || !config) {
      clearRetryTimer();
      setMountedRequestKey(0);
      setNextRequestAt(null);
      setCanRequestOnNavigation(false);
      setStatus("idle");
      cooldownStartedRequestKeyRef.current = 0;
      lastHandledNavigationRequestKeyRef.current = requestKey;
      return;
    }

    // 首次进入 m-qs 页面时预加载一条 interstitial；
    // 后续在广告填充成功并冷却结束后，等待下一次切页再请求。
    if (mountedRequestKey === 0 && status === "idle" && requestSeqRef.current === 0) {
      beginRequest();
    }
  }, [beginRequest, clearRetryTimer, config, enabled, mountedRequestKey, requestKey, status]);

  useEffect(() => {
    if (!enabled || !config || requestKey <= 0) {
      return;
    }

    if (requestKey === lastHandledNavigationRequestKeyRef.current) {
      return;
    }

    lastHandledNavigationRequestKeyRef.current = requestKey;
    const currentTime = Date.now();
    const cooldownExpired = !!nextRequestAt && currentTime >= nextRequestAt;

    if (cooldownExpired) {
      clearRetryTimer();
      setNextRequestAt(null);
      setCanRequestOnNavigation(true);
    }

    if ((nextRequestAt && !cooldownExpired) || (!canRequestOnNavigation && !cooldownExpired)) {
      return;
    }

    beginRequest();
  }, [beginRequest, canRequestOnNavigation, clearRetryTimer, config, enabled, nextRequestAt, requestKey]);

  useEffect(() => {
    updateMqsInterstitialGate(status, mountedRequestKey);
  }, [mountedRequestKey, status]);

  useEffect(() => {
    return () => {
      clearRetryTimer();
      resetMqsInterstitialGate();
    };
  }, [clearRetryTimer]);

  const shouldShowDebugOverlay =
    enabled &&
    getSearchParam(MQS_INTERSTITIAL_DEBUG_QUERY) === "1" &&
    (MQS_DEBUG_HOSTS.includes(window.location.hostname) || isLocalDebugHost(window.location.hostname));

  const interstitialConfig = useMemo(
    () => ({
      requireStorageAccess: false,
      // 只保留 GPT 默认的页面 <a> 链接点击触发；其他可配置触发全部关闭，
      // 避免切回窗口、页面空闲、文章到底部或浏览器导航动作误触 m-qs 插屏。
      triggers: {
        navBar: false,
        unhideWindow: false,
        inactivity: false,
        endOfArticle: false,
        backward: false,
      },
    }),
    [],
  );

  const handleRequestFilled = useCallback(() => {
    setStatus("ready");
    if (mountedRequestKey > 0) {
      // m-qs 的下一轮请求改为“填充成功即开始冷却”，
      // 但当前 slot 继续保留，让 GPT 仍可按触发器正常展示。
      scheduleNextNavigationRequest(MQS_INTERSTITIAL_COOLDOWN_MS, mountedRequestKey);
    }
  }, [mountedRequestKey, scheduleNextNavigationRequest]);

  const handleRequestEmpty = useCallback(() => {
    // 首次请求只打一次；如果当前无填充，不在本轮页面生命周期里自动补请求。
    stopRequesting("empty");
  }, [stopRequesting]);

  const handleUnsupported = useCallback(() => {
    stopRequesting("unsupported");
  }, [stopRequesting]);

  const handleImpressionViewable = useCallback(() => {
    setStatus("showing");
  }, []);

  const handleClosed = useCallback(() => {
    // 插屏真实关闭后立即释放 gate，让普通广告恢复请求；
    // 冷却计时仍沿用“填充成功时已启动”的 nextRequestAt。
    releaseCurrentInterstitial("idle");
  }, [releaseCurrentInterstitial]);

  useEffect(() => {
    if (!shouldShowDebugOverlay && !nextRequestAt) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [nextRequestAt, shouldShowDebugOverlay]);

  return (
    <>
      {mountedRequestKey > 0 ? (
        <GoogleAd1
          key={`mqs-interstitial-${mountedRequestKey}`}
          {...config}
          interstitialConfig={interstitialConfig}
          onRequestFilled={handleRequestFilled}
          onRequestEmpty={handleRequestEmpty}
          onUnsupported={handleUnsupported}
          onImpressionViewable={handleImpressionViewable}
          onClosed={handleClosed}
        />
      ) : null}
      {shouldShowDebugOverlay ? (
        <MqsInterstitialDebugOverlay
          status={status}
          lastRequestAt={lastRequestAt}
          nextRequestAt={nextRequestAt}
          now={now}
          canRequestOnNavigation={canRequestOnNavigation}
        />
      ) : null}
    </>
  );
}
